/**
 * `/docs/pixel` — install the Clickweave pixel (real-user analytics).
 *
 * The non-developer on-ramp: paste one script tag, load the page,
 * watch beacons arrive. The snippet pre-fills the active site's id
 * so most users copy-paste without editing anything. The RUM
 * placeholder banners and the audits SetupCard link here — keep this
 * page free of API-key/cURL content; that lives in `/docs/quickstart`.
 */
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import type { BreadcrumbEntry } from '@/components/shell/Breadcrumb';
import { Island } from '@/components/shell/Island';
import { SiteSwitcherCrumb } from '@/components/shell/SiteSwitcherCrumb';
import { Button } from '@/components/ui/button';
import { useActiveSite } from '@/lib/active-site';
import { ChartLineUp } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/docs/pixel')({
  component: PixelDocs,
});

/** Same source the deploy workflow injects into our own index.html. */
const PIXEL_ORIGIN: string = import.meta.env.PUBLIC_PIXEL_ENDPOINT ?? 'https://pixel.clickweave.co';

const SNIPPET =
  '<script\n' +
  '  async\n' +
  '  defer\n' +
  '  src="$PIXEL_ORIGIN/v1/pixel.js"\n' +
  '  data-site-id="$SITE_ID"\n' +
  '  data-endpoint="$PIXEL_ORIGIN/v1/beacon"\n' +
  '></script>';

function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}

function PixelDocs(): ReactElement {
  const { site } = useActiveSite();
  const host = hostOf(site?.url);

  // Unlike the other docs pages this one is site-scoped — the snippet
  // embeds the active site's id — so it carries the site-switcher
  // crumb like Traffic/Performance do, in the same leftmost slot.
  // With no active site (fresh org) fall back to the plain docs
  // breadcrumb; the snippet shows <YOUR_SITE_ID> in that case anyway.
  const breadcrumb: BreadcrumbEntry[] = [
    ...(site ? [{ label: site.name, render: <SiteSwitcherCrumb /> }] : []),
    { label: 'Docs', href: '/docs' },
    { label: 'Install the pixel' },
  ];

  return (
    <Island breadcrumb={breadcrumb}>
      <div className="mx-auto max-w-3xl space-y-10">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-[var(--color-ink)]">Install the pixel</h1>
          <p className="text-sm text-[var(--color-mute)]">
            One script tag turns on real-user analytics — traffic, sources, countries, devices, and
            the Core Web Vitals your visitors actually experience. The whole script is ~7 KB: no
            cookies, no PII, and it honours Global Privacy Control and Do Not Track.
          </p>
        </header>

        <Step
          number={1}
          title="Paste the snippet"
          body={
            <>
              <p className="text-sm text-[var(--color-mute)]">
                Add this tag to the <span className="font-mono text-xs">&lt;head&gt;</span> of every
                page — a shared layout, template, or tag manager all work.
                {host ? (
                  <>
                    {' '}
                    It's pre-filled for <span className="font-mono text-xs">{host}</span>; use the
                    site switcher in the breadcrumb above to install on another site.
                  </>
                ) : (
                  <>
                    {' '}
                    Replace <span className="font-mono text-xs">&lt;YOUR_SITE_ID&gt;</span> with the
                    site id shown in your site settings.
                  </>
                )}
              </p>
              <CodeSnippet
                code={SNIPPET}
                variables={{
                  PIXEL_ORIGIN,
                  SITE_ID: site?.uuid ?? '<YOUR_SITE_ID>',
                }}
              />
            </>
          }
        />

        <Step
          number={2}
          title="Load your site once"
          body={
            <>
              <p className="text-sm text-[var(--color-mute)]">
                That's the whole install. Deploy, open your site in a browser, and the first beacons
                land within about 60 seconds — Traffic and Performance light up on the next refresh.
              </p>
              <Button size="sm" variant="outline" render={<Link to="/traffic" />}>
                <ChartLineUp aria-hidden="true" />
                Open Traffic
              </Button>
            </>
          }
        />

        <p className="text-xs text-[var(--color-mute)]">
          Automating audits instead? The Clickweave CLI (<span className="font-mono">cw</span>) is
          the fastest developer path; the raw HTTP flow lives in the{' '}
          <Link to="/docs/quickstart" className="underline">
            API quickstart
          </Link>
          .
        </p>
      </div>
    </Island>
  );
}

function Step({
  number,
  title,
  body,
}: {
  number: number;
  title: string;
  body: ReactElement;
}): ReactElement {
  return (
    <section className="space-y-3">
      <header className="flex items-center gap-3">
        <span className="flex size-7 items-center justify-center rounded-full bg-[var(--color-ink)] text-xs font-semibold text-[var(--color-paper)]">
          {number}
        </span>
        <h2 className="text-lg font-semibold text-[var(--color-ink)]">{title}</h2>
      </header>
      <div className="space-y-3 pl-10">{body}</div>
    </section>
  );
}
